<template>
  <v-container fluid>
    <v-card>
      <v-card-title>
        Blog Categories
        <v-spacer />
        <v-btn text class="mr-2" :to="{ name: 'Blogs' }">Back to blogs</v-btn>
        <v-btn color="primary" @click="openForm()">
          <v-icon left>mdi-plus</v-icon>New category
        </v-btn>
      </v-card-title>

      <v-data-table :headers="headers" :items="categories" :loading="loading">
        <template v-slot:item.color="{ item }">
          <span class="color-dot" :style="{ backgroundColor: item.color }" />
          {{ item.color }}
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn icon small @click="openForm(item)">
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon small color="error" @click="askDelete(item)">
            <v-icon small>mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="formDialog" max-width="420" persistent>
      <v-card>
        <v-card-title>
          {{ editingId ? "Edit category" : "New category" }}
        </v-card-title>
        <v-card-text>
          <v-form ref="form" @submit.prevent="save">
            <v-text-field
              v-model="name"
              label="Name"
              counter="100"
              outlined
              dense
              autofocus
              :rules="[
                (v) => !!v || 'Required',
                (v) => !v || v.length <= 100 || 'Max 100 characters',
              ]"
              :error-messages="errors.name"
            />

            <color-picker-field
              v-model="color"
              :error-messages="errors.color"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="saving" @click="formDialog = false">
            Cancel
          </v-btn>
          <v-btn color="primary" :loading="saving" @click="save">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="420">
      <v-card>
        <v-card-title class="headline">Delete category?</v-card-title>
        <v-card-text>
          "{{ toDelete && toDelete.name }}" will be permanently deleted.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="saving" @click="deleteDialog = false">
            Cancel
          </v-btn>
          <v-btn color="error" :loading="saving" @click="confirmDelete">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="4000">
      {{ snackbar.text }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapState, mapActions } from "vuex";
import ColorPickerField from "@/components/blogs/ColorPickerField.vue";

const DEFAULT_COLOR = "#2F7D0F";

export default {
  name: "BlogCategories",
  components: { ColorPickerField },
  data() {
    return {
      headers: [
        { text: "Name", value: "name" },
        { text: "Slug", value: "slug" },
        { text: "Color", value: "color", sortable: false },
        { text: "Blogs", value: "blogs_count" },
        { text: "Actions", value: "actions", sortable: false, align: "end" },
      ],
      formDialog: false,
      editingId: null,
      name: "",
      color: DEFAULT_COLOR,
      deleteDialog: false,
      toDelete: null,
      snackbar: { show: false, text: "", color: "success" },
    };
  },
  computed: {
    ...mapState("blogs", [
      "categories",
      "loading",
      "saving",
      "error",
      "errors",
    ]),
  },
  created() {
    this.getCategories();
  },
  methods: {
    ...mapActions("blogs", [
      "getCategories",
      "createCategory",
      "updateCategory",
      "deleteCategory",
    ]),

    notify(text) {
      this.snackbar = { show: true, text, color: "success" };
    },

    openForm(item) {
      this.editingId = item ? item.id : null;
      this.name = item ? item.name : "";
      this.color = item && item.color ? item.color : DEFAULT_COLOR;
      this.formDialog = true;
      this.$nextTick(
        () => this.$refs.form && this.$refs.form.resetValidation(),
      );
    },

    async save() {
      if (!this.$refs.form.validate()) return;

      const data = new FormData();
      data.append("name", this.name.trim());
      data.append("color", this.color);

      if (this.editingId) {
        await this.updateCategory({ id: this.editingId, data });
      } else {
        await this.createCategory(data);
      }

      // error === null means the request succeeded
      if (this.error === null) {
        this.formDialog = false;
        this.notify("Category saved.");
        this.getCategories();
      }
    },

    askDelete(item) {
      this.toDelete = item;
      this.deleteDialog = true;
    },

    async confirmDelete() {
      await this.deleteCategory(this.toDelete.id);
      this.deleteDialog = false;

      if (this.error === null) {
        this.notify("Category deleted.");
      }
      // A category that still has blogs is reported by the root pop up error.
      this.getCategories();
    },
  },
};
</script>

<style scoped>
.color-dot {
  display: inline-block;
  width: 14px;
  height: 14px;
  margin-right: 6px;
  vertical-align: middle;
  border-radius: 4px;
  border: 1px solid rgba(128, 128, 128, 0.6);
}
</style>
